import React, {useEffect, useMemo, useState} from "react";
import {useLocation} from "react-router-dom";
import {useAuth} from "hooks/useAuth";
import {can} from "data/permissions";

const STAGING_HOSTS = new Set([
  "coverbaker-staging.labs.jb.gg",
  "baker.labs.jb.gg",
  "localhost",
]);

const STORAGE_KEY = "stagingNoticeAckUntil";
const EIGHT_HOURS_MS = 8 * 60 * 60 * 1000;

export default function StagingNotice() {
  const {role, isLoading} = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const isLoginPage = location.pathname === "/login" || location.pathname.startsWith("/login/");

  const isStaging = useMemo(() => {
    if (typeof window === "undefined") return false;
    return STAGING_HOSTS.has(window.location.hostname);
  }, []);

  useEffect(() => {
    if (isLoading || isLoginPage) return;

    if (!isStaging) {
      setIsOpen(false);
      return;
    }

    if (can(role, "skipStagingNotice")) {
      setIsOpen(false);
      return;
    }

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const ackUntil = raw ? Number(raw) : 0;
      const now = Date.now();
      setIsOpen(!(Number.isFinite(ackUntil) && now <= ackUntil));
    } catch (_) {
      setIsOpen(true);
    }
  }, [isLoading, role, isLoginPage, isStaging]);

  if (!isOpen) return null;

  const handleOpenMain = () => {
    window.location.href = "https://coverbaker.labs.jb.gg";
  };

  const handleOpenExperimental = () => {
    try {
      const until = Date.now() + EIGHT_HOURS_MS;
      localStorage.setItem(STORAGE_KEY, String(until));
    } catch (_) {
      // ignore storage errors; still close the dialog
    }
    setIsOpen(false);
  };

  const overlayStyle = {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  };

  const dialogStyle = {
    background: "#1e1f22",
    color: "#fff",
    width: "min(560px, 92vw)",
    borderRadius: 12,
    boxShadow: "0 12px 32px rgba(0,0,0,0.5)",
    border: "1px solid #2b2d30",
  };

  const contentStyle = {
    padding: "20px 24px",
    lineHeight: 1.5,
    fontSize: 14,
  };

  const actionsStyle = {
    display: "flex",
    gap: 12,
    justifyContent: "flex-end",
    padding: "12px 16px 16px",
  };

  const primaryBtnStyle = {
    appearance: "none",
    border: 0,
    background: "#4c8bf5",
    color: "#fff",
    padding: "8px 14px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 600,
  };

  const secondaryBtnStyle = {
    appearance: "none",
    border: "1px solid #3a3c40",
    background: "transparent",
    color: "#fff",
    padding: "8px 14px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 600,
  };

  return (
    <div style={overlayStyle} role="presentation">
      <div role="dialog" aria-modal="true" aria-labelledby="staging-dialog-title" style={dialogStyle}>
        <div style={contentStyle}>
          <h3 id="staging-dialog-title" style={{margin: 0, fontSize: 16, fontWeight: 700}}>Staging environment notice</h3>
          <p style={{marginTop: 12}}>
            You opened the staging page — unless you were explicitly asked by developers to open this
            specific link, please go to https://coverbaker.labs.jb.gg instead.
          </p>
        </div>
        <div style={actionsStyle}>
          <button type="button" onClick={handleOpenMain} style={primaryBtnStyle}>
            Open main Coverbaker page
          </button>
          <button type="button" onClick={handleOpenExperimental} style={secondaryBtnStyle}>
            Open experimental Coverbaker page
          </button>
        </div>
      </div>
    </div>
  );
}


